<template>
	<section>
		<!-- barra breadcrumb + bottone "nuovo" -->
		<header class="breadcrumb">
			<span>Devices</span>
			<div class="breadcrumb-actions">
				<button id="btn-new-device" class="btn-new" @click="newDevice">
					+ Nuovo device
				</button>
				<RouterLink
					v-if="isAdmin"
					class="btn-new"
					:to="{ name: 'ArchivedDevices' }"
				>
					Archiviati
				</RouterLink>

				<NotificationBell v-if="showCrumbBell" placement="header" />
			</div>
		</header>

		<!-- messaggi -->
		<p v-if="loading" class="msg">Caricamento…</p>
		<p v-else-if="error" class="msg error">
			{{ error }}
		</p>

		<p v-else-if="!devices.length && !loading && !error" class="msg">
			Nessun dispositivo presente
		</p>

		<!-- tabella -->
		<div v-else class="dashboard-wrapper">
			<!-- Gmail-style Selection Bar -->
			<Transition name="slide-fade">
				<div v-if="selectedIds.length > 0" class="selection-bar">
					<div class="selection-info">
						<button
							class="btn-close-selection"
							@click="selectedIds = []"
							title="Annulla selezione"
						>
							<XIcon />
						</button>
						<span class="selection-count"
							>{{ selectedIds.length }} selezionati</span
						>
					</div>
					<div class="selection-actions">
						<button
							class="btn-selection-action archive"
							@click="openBulkArchiveModal"
							title="Archivia selezionati"
						>
							<TrashIcon />
							<span>Archivia</span>
						</button>
					</div>
				</div>
			</Transition>

			<div
				class="tbl-wrap"
				:style="{ marginTop: selectedIds.length > 0 ? '10px' : '50px' }"
				v-show="devices.length"
			>
				<table class="tbl">
					<thead id="devices-list">
						<tr>
							<th class="select-col">
								<input
									type="checkbox"
									:checked="isAllSelected"
									@change="toggleSelectAll"
									:title="
										isAllSelected
											? 'Deseleziona tutti i device'
											: 'Seleziona tutti i device'
									"
								/>
							</th>
							<th @click="toggleSort('id')" class="sortable">
								<div class="header-content">
									ID
									<component
										:is="
											sortBy === 'id'
												? sortOrder === 'asc'
													? ChevronUpIcon
													: ChevronDownIcon
												: 'span'
										"
										class="sort-icon"
									/>
								</div>
							</th>
							<th>Nome</th>
							<th>Status</th>
							<th>Moduli connessi</th>
							<th @click="toggleSort('updatedAt')" class="sortable">
								<div class="header-content">
									Last Update
									<component
										:is="
											sortBy === 'updatedAt'
												? sortOrder === 'asc'
													? ChevronUpIcon
													: ChevronDownIcon
												: 'span'
										"
										class="sort-icon"
									/>
								</div>
							</th>
							<th v-if="isAdmin">Proprietario</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<!-- Search Row -->
						<tr class="search-row">
							<td :colspan="isAdmin ? 8 : 7">
								<div class="search-container">
									<input
										v-model="searchQuery"
										type="text"
										placeholder="Cerca per ID, Nome o Status..."
										class="search-input"
									/>
								</div>
							</td>
						</tr>
						<tr
							v-for="(d, i) in paginatedDevices"
							:key="d.id"
							:class="i % 2 && 'alt'"
						>
							<td class="select-col">
								<input
									type="checkbox"
									:value="d.id"
									v-model="selectedIds"
									:title="
										selectedIds.includes(d.id)
											? 'Deseleziona questo device'
											: 'Seleziona questo device'
									"
								/>
							</td>
							<td class="mono">{{ d.id }}</td>
							<td>
								<div class="device-cell-content">
									<div class="device-info-left">
										<span class="device-name-text">{{ d.name || "—" }}</span>
										<span
											v-if="d.is_shared"
											class="shared-device-badge"
											:title="`Condiviso da ${d.shared_by_name || 'Admin'}`"
										>
											🔗 Condiviso
										</span>
										<button
											v-if="d.name"
											class="rename-btn"
											title="Rinomina"
											@click="rename(d)"
										>
											<PenLineIcon />
										</button>
									</div>

									<div class="device-info-actions">
										<div class="editor-menu-wrapper">
											<button
												class="btn-action-labeled"
												title="Scegli Editor"
												@click.stop="toggleEditorMenu(d.id)"
											>
												<EditIcon />
												<span>Editor</span>
												<ChevronDownIcon class="chevron-icon" />
											</button>

											<div
												v-if="openEditorMenuId === d.id"
												class="dropdown-menu"
											>
												<button class="menu-item" @click="edit(d)">
													<span>Logica</span>
												</button>
												<button class="menu-item" @click="openSchema(d)">
													<span>Schema</span>
												</button>
											</div>
										</div>
										<button
											class="btn-action-labeled"
											title="Data"
											@click="openData(d)"
										>
											<DatabaseIcon />
											<span>Data</span>
										</button>
									</div>
								</div>
							</td>
							<td>
								<button
									class="status-btn"
									@click="openStatusModal(d)"
									:title="`Cambia stato (attuale: ${d.status})`"
								>
									<!-- pallino -->
									<span class="status-dot" :class="getStatusClass(d.status)" />

									<!-- icona matita (già importata in alto) -->
									<ChevronDownIcon class="status-edit-icon" />
								</button>
							</td>

							<td>{{ listModules(d) }}</td>
							<td>{{ formatDate(d.updatedAt) }}</td>

							<!-- ⬇️ badge proprietario, visibile solo agli admin -->
							<td v-if="isAdmin">
								<span
									class="owner-badge"
									:class="d.user?.is_admin ? 'admin' : 'user'"
								>
									<span class="owner-role">{{
										d.user?.is_admin ? "Admin" : "User"
									}}</span>
									<span class="owner-sep">•</span>
									<span class="owner-name">{{ ownerDisplayName(d) }}</span>
									<span class="owner-id">(#{{ d.user?.id ?? d.user_id }})</span>
								</span>
							</td>

							<td class="actions">
								<template v-if="selectedIds.length === 0">
									<button title="Cronologia" @click="log(d)">
										<ClockIcon />
									</button>
									<button
										class="btn-action-archive"
										title="Archivia"
										@click="del(d)"
									>
										<TrashIcon />
									</button>
								</template>
							</td>
						</tr>
						<!-- Riga nessun risultato -->
						<tr v-if="searchQuery && !filteredDevices.length">
							<td
								:colspan="isAdmin ? 8 : 7"
								class="msg-row"
								style="
									text-align: center;
									padding: 24px;
									color: #666;
									background: #fff;
								"
							>
								Nessun risultato trovato per "{{ searchQuery }}"
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Paginazione -->
			<div class="pagination-controls" v-if="totalPages > 1">
				<button
					class="btn-pagination"
					:disabled="currentPage === 1"
					@click="currentPage = 1"
					title="Vai alla prima pagina"
				>
					<font-awesome-icon :icon="faAnglesLeft" />
				</button>
				<button
					class="btn-pagination"
					:disabled="currentPage === 1"
					@click="currentPage--"
					title="Vai alla pagina precedente"
				>
					<font-awesome-icon :icon="faAngleLeft" />
				</button>

				<div class="pagination-jump">
					<span>Pagina</span>
					<input
						type="number"
						v-model="currentPageInput"
						@keyup.enter="jumpToPage"
						@blur="jumpToPage"
						min="1"
						:max="totalPages"
						class="jump-input"
					/>
					<span>di {{ totalPages }}</span>
				</div>

				<button
					class="btn-pagination"
					:disabled="currentPage === totalPages"
					@click="currentPage++"
					title="Vai alla pagina successiva"
				>
					<font-awesome-icon :icon="faAngleRight" />
				</button>
				<button
					class="btn-pagination"
					:disabled="currentPage === totalPages"
					@click="currentPage = totalPages"
					title="Vai all'ultima pagina"
				>
					<font-awesome-icon :icon="faAnglesRight" />
				</button>
			</div>
		</div>

		<!-- Modale Cambio Stato -->
		<div v-if="showStatusModal" class="modal-mask">
			<div class="modal">
				<h3>Cambio stato dispositivo</h3>
				<p style="margin: 6px 0 18px">
					Seleziona il nuovo stato per
					<strong>{{ selectedDevice?.name || selectedDevice?.id }}</strong>
				</p>

				<div class="status-options">
					<button
						v-for="status in statusOptions"
						:key="status.value"
						@click="selectStatus(status.value)"
						:class="{ active: tempStatus === status.value }"
					>
						<span class="status-dot" :class="status.class" />
						{{ status.label }}
					</button>
				</div>

				<div class="modal-actions">
					<!-- <ClockIcon
            class="clock-icon"
            @click="goToRecords"
            title="Vai ai record del device"
          /> -->
					<button
						:disabled="!tempStatus || statusLoading"
						@click="confirmStatusChange"
					>
						Conferma
					</button>
					<button :disabled="statusLoading" @click="cancelStatusChange">
						Annulla
					</button>
				</div>
			</div>
		</div>

		<!-- modale Rinominazione -->
		<div v-if="showRenameModal" class="modal-mask">
			<div class="modal">
				<h3>Nome del device</h3>
				<input
					v-model="tempName"
					class="modal-input"
					placeholder="Es. Weather-Station-01"
				/>
				<div class="modal-actions">
					<button @click="confirmRename">OK</button>
					<button @click="cancelRename">Annulla</button>
				</div>
			</div>
		</div>

		<!-- modale Archiviazione -->
		<div v-if="showArchiveModal" class="modal-mask">
			<div class="modal">
				<h3>
					{{
						isBulkArchive ? "Archivia devices selezionati" : "Archivia device"
					}}
				</h3>
				<p style="margin: 6px 0 18px">
					<template v-if="selectedIds.length > 1">
						Vuoi davvero archiviare i
						<strong>{{ selectedIds.length }}</strong> dispositivi selezionati?
					</template>
					<template v-else>
						Vuoi davvero archiviare il device
						<strong>{{
							selectedDevice?.name ||
							selectedDevice?.id ||
							(selectedIds.length === 1 ? selectedIds[0] : "")
						}}</strong
						>?
					</template>
				</p>
				<div class="modal-actions">
					<button :disabled="archiveLoading" @click="confirmArchive">OK</button>
					<button :disabled="archiveLoading" @click="cancelArchive">
						Annulla
					</button>
				</div>
			</div>
		</div>

		<!-- Modale Cronologia -->
		<div v-if="showHistoryModal" class="modal-mask">
			<div
				class="modal history-modal"
				ref="historyContainer"
				@scroll="onScroll"
			>
				<!-- header sticky -->
				<div class="modal-header">
					<h3>
						Cronologia del device '{{
							selectedDevice?.name || selectedDevice?.id
						}}'
					</h3>

					<!-- bottone di chiusura -->
					<button
						class="modal-close"
						@click="showHistoryModal = false"
						title="Chiudi"
					>
						<XIcon />
					</button>
				</div>

				<div v-if="historyLoading" class="msg">Caricamento cronologia...</div>

				<div v-else class="history-content">
					<div v-if="!deviceHistory.length" class="msg">
						Nessun evento registrato
					</div>

					<div v-else>
						<div class="history-section">
							<h4>Informazioni Base</h4>
							<div class="info-grid">
								<div class="info-item">
									<span class="info-label">Creato il:</span>
									<span class="info-value">{{
										formatDate(selectedDevice.createdAt)
									}}</span>
								</div>

								<div class="info-item">
									<span class="info-label">Ultimo aggiornamento:</span>
									<span class="info-value">{{
										formatDate(selectedDevice.updatedAt)
									}}</span>
								</div>
								<div class="info-item">
									<span class="info-label">Stato attuale:</span>

									<span class="info-value">
										<span
											class="status-dot"
											:class="getStatusClass(selectedDevice.status)"
										></span>
										<span>{{ selectedDevice.status || "online" }}</span>
									</span>
								</div>
							</div>
						</div>

						<div class="history-section">
							<h4>Eventi</h4>
							<!-- filtri -->
							<div class="filters">
								<label v-for="f in filters" :key="f.key" class="filter-item">
									<input
										type="checkbox"
										v-model="activeFilters"
										:value="f.key"
									/>
									{{ f.label }}
								</label>
								<button
									v-if="activeFilters.length"
									class="filters-reset"
									@click="activeFilters = []"
								>
									Azzera filtri
								</button>
							</div>

							<!-- lista eventi -->
							<div class="events-list">
								<div v-if="!filteredHistory.length" class="msg">
									Nessun evento corrispondente ai filtri correnti
								</div>

								<template v-else>
									<div
										v-for="event in filteredHistory"
										:key="event.id"
										class="event-item"
										:class="[
											event.type,
											event.processedType || event.eventType,
										]"
									>
										<div class="event-time">
											{{ formatDate(event.timestamp) }}
										</div>
										<div class="event-content">
											<div class="event-header">
												<span class="event-type">{{
													getEventTypeLabel(event)
												}}</span>
												<span class="event-action">{{
													getActionLabel(event)
												}}</span>
											</div>
											<div class="event-details">
												<template v-if="event.type === 'status'">
													<span>da </span>
													<span
														class="status-value"
														:class="getStatusClass(event.details.oldStatus)"
														>{{ event.details.oldStatus }}</span
													>
													<span> a </span>
													<span
														class="status-value"
														:class="getStatusClass(event.details.newStatus)"
														>{{ event.details.newStatus }}</span
													>
												</template>

												<template
													v-else-if="
														event.type === 'node' &&
														event.processedType === 'moved'
													"
												>
													<!-- <span>Modulo </span> -->
													<strong>{{
														event.details.label || event.nodeId
													}}</strong>
													<span>
														spostato da ({{ event.oldPosition.x }},
														{{ event.oldPosition.y }})</span
													>
													<span>
														a ({{ event.details.x }},
														{{ event.details.y }})</span
													>
												</template>

												<!-- <template v-else-if="event.type === 'node'">
												<span>Modulo </span>
												<strong>{{ event.details.label || event.nodeId }}</strong>
												<span v-if="event.eventType === 'added'"> aggiunto in posizione ({{ event.details.x }}, {{ event.details.y }})</span>
												<span v-if="event.eventType === 'removed'"> rimosso dalla posizione ({{ event.details.x }}, {{ event.details.y }})</span>
											</template> -->

												<template v-else-if="event.type === 'connection'">
													<!-- <span>Connessione </span> -->
													<!-- <span class="event-action">{{ getActionLabel(event) }}</span> -->
													<span
														v-if="
															event.details.display_source ===
															event.details.display_target
														"
													>
														dallo
													</span>
													<span v-else> tra </span>

													<template
														v-if="
															event.details.display_source ===
															event.details.display_target
														"
													>
														<strong>stesso modulo</strong>
													</template>
													<template v-else>
														<strong>{{ event.details.display_source }}</strong>
														<span> e </span>
														<strong>{{ event.details.display_target }}</strong>
													</template>

													<template
														v-if="
															event.details.display_output !== '—' ||
															event.details.display_input !== '—'
														"
													>
														<span> (</span>
														<template
															v-if="event.details.display_output !== '—'"
														>
															<span>output: </span
															><span class="connection-detail">{{
																event.details.display_output
															}}</span>
														</template>
														<template
															v-if="
																event.details.display_output !== '—' &&
																event.details.display_input !== '—'
															"
														>
															<span>, </span>
														</template>
														<template
															v-if="event.details.display_input !== '—'"
														>
															<span>input: </span
															><span class="connection-detail">{{
																event.details.display_input
															}}</span>
														</template>
														<span>)</span>
													</template>
												</template>

												<template
													v-else-if="
														event.type === 'node' &&
														event.processedType === 'moved'
													"
												>
													<!-- <span>Modulo </span> -->
													<strong>{{
														event.details.label || event.nodeId
													}}</strong>
													<span>
														spostato da ({{ event.oldPosition.x }},
														{{ event.oldPosition.y }})</span
													>
													<span>
														a ({{ event.details.x }},
														{{ event.details.y }})</span
													>
												</template>

												<template v-else-if="event.type === 'node'">
													<!-- <span>Modulo </span> -->
													<strong>{{
														event.details.label || event.nodeId
													}}</strong>
													<span v-if="event.eventType === 'added'">
														aggiunto in posizione ({{ event.details.x }},
														{{ event.details.y }})</span
													>
													<span v-if="event.eventType === 'removed'">
														rimosso dalla posizione ({{ event.details.x }},
														{{ event.details.y }})</span
													>
												</template>
											</div>
										</div>
									</div>
								</template>
							</div>
						</div>
					</div>
					<!-- 
				<div class="modal-actions">
					<button @click="showHistoryModal = false">Chiudi</button>
				</div> -->
				</div>

				<!-- scroll-to-top -->
				<div v-if="showScrollTop" class="scroll-top-wrap">
					<button
						v-if="showScrollTop"
						class="scroll-top-btn"
						@click="scrollToTop"
						title="Torna all’inizio"
					>
						<ChevronUpIcon />
					</button>
				</div>
			</div>
		</div>
	</section>

	<!-- Permission Request Modal -->
	<PermissionRequestModal
		v-if="showPermissionModal"
		:deviceId="permissionDevice?.id"
		:deviceName="permissionDevice?.name"
		:permissionType="failedPermissionType"
		@close="showPermissionModal = false"
	/>

	<!-- Modale Nuovo Device -->
	<div v-if="showNewDeviceModal" class="modal-mask">
		<div class="modal new-device-modal">
			<div class="modal-header">
				<h3 style="margin-bottom: 0">
					{{
						newDeviceStep === 1
							? "Nuovo Device - Step 1/2"
							: "Nuovo Device - Step 2/2"
					}}
				</h3>
				<button class="modal-close" @click="showNewDeviceModal = false">
					<XIcon />
				</button>
			</div>

			<div class="modal-content" style="margin-top: 20px">
				<!-- Step 1 -->
				<div v-if="newDeviceStep === 1" class="step-container">
					<div class="form-group">
						<label class="modal-label">Email</label>
						<div style="display: flex; gap: 10px">
							<input
								v-model="newDeviceForm.email"
								type="email"
								placeholder="Conferma l'email"
								class="modal-input"
								:disabled="isOtpSent || otpRequestLoading"
							/>
							<button
								v-if="!isOtpSent"
								@click="requestOtp"
								:disabled="!newDeviceForm.email || otpRequestLoading"
								class="btn-otp"
							>
								<LoaderIcon v-if="otpRequestLoading" class="animate-spin" />
								<SendIcon v-else />
								<span>{{
									otpRequestLoading ? "Invio..." : "Invia Codice OTP"
								}}</span>
							</button>
						</div>
					</div>
					<div class="form-group" style="margin-top: 15px">
						<label class="modal-label">Codice OTP</label>
						<input
							v-model="newDeviceForm.otp"
							type="text"
							placeholder="Inserisci il codice OTP ricevuto via email"
							class="modal-input"
							:disabled="!isOtpSent || otpVerifyLoading"
						/>
					</div>
				</div>

				<!-- Step 2 -->
				<div v-if="newDeviceStep === 2" class="step-container">
					<div class="form-group">
						<label class="modal-label">NOME</label>
						<input
							v-model="newDeviceForm.name"
							type="text"
							placeholder="Inserisci il nome del device"
							class="modal-input"
						/>
						<p class="field-hint">
							Puoi modificarlo ora o successivamente dalla lista device.
						</p>
					</div>
					<div class="form-group" style="margin-top: 15px">
						<label class="modal-label">TIPOLOGIA</label>
						<div
							class="radio-group"
							style="display: flex; gap: 20px; margin-top: 8px"
						>
							<label
								class="radio-label"
								style="
									display: flex;
									align-items: center;
									gap: 8px;
									cursor: pointer;
								"
							>
								<input
									type="radio"
									value="static"
									v-model="newDeviceForm.type"
									style="width: 18px; height: 18px; accent-color: #008f8f"
								/>
								Statico
							</label>
							<label
								class="radio-label"
								style="
									display: flex;
									align-items: center;
									gap: 8px;
									cursor: pointer;
								"
							>
								<input
									type="radio"
									value="dynamic"
									v-model="newDeviceForm.type"
									style="width: 18px; height: 18px; accent-color: #008f8f"
								/>
								Dinamico
							</label>
						</div>
					</div>
					<div v-if="newDeviceForm.type === 'static'" class="form-group" style="margin-top: 15px">
						<label class="modal-label">POSIZIONE (opzionale)</label>
						<div style="display: flex; gap: 10px; margin-top: 8px">
							<input
								v-model="newDeviceForm.lat"
								type="number"
								step="any"
								placeholder="Latitudine"
								class="modal-input"
							/>
							<input
								v-model="newDeviceForm.lng"
								type="number"
								step="any"
								placeholder="Longitudine"
								class="modal-input"
							/>
						</div>
						<p class="field-hint">Puoi impostarla ora o successivamente dalla Dashboard.</p>
					</div>

				<div class="form-group" style="margin-top: 15px">
						<label class="modal-label">MODULI</label>
						<div
							class="checkbox-group"
							style="
								display: grid;
								grid-template-columns: repeat(2, 1fr);
								gap: 10px;
								margin-top: 8px;
							"
						>
							<label
								v-for="mod in ['sat', 'lora', 'lte', 'plc']"
								:key="mod"
								class="checkbox-label"
								style="
									display: flex;
									align-items: center;
									gap: 8px;
									cursor: pointer;
								"
							>
								<input
									type="checkbox"
									:value="mod"
									v-model="newDeviceForm.modules"
									style="width: 18px; height: 18px; accent-color: #008f8f"
								/>
								{{ mod.toUpperCase() }}
							</label>
						</div>
					</div>
				</div>
			</div>

			<div class="modal-actions" style="margin-top: 30px">
				<button
					v-if="newDeviceStep === 2"
					class="btn-secondary"
					@click="newDeviceStep = 1"
					style="background: transparent; border: 1px solid #444; color: #ccc"
				>
					Indietro
				</button>
				<button
					v-if="newDeviceStep === 1"
					@click="nextNewDeviceStep"
					:disabled="!isOtpSent || otpVerifyLoading"
				>
					{{ otpVerifyLoading ? "Verifica..." : "Avanti" }}
				</button>
				<button v-else @click="confirmNewDevice">Crea Device</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import { API_URL, EDITOR_BASE_URL } from "@/config";
import dayjs from "dayjs";
import {
	ChevronDown as ChevronDownIcon,
	ChevronUp as ChevronUpIcon,
	Clock as ClockIcon,
	Database as DatabaseIcon,
	Edit as EditIcon,
	PenLine as PenLineIcon,
	Trash as TrashIcon,
	X as XIcon,
	Send as SendIcon,
	Loader2 as LoaderIcon,
} from "lucide-vue-next";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
	faAngleLeft,
	faAngleRight,
	faAnglesLeft,
	faAnglesRight,
} from "@fortawesome/free-solid-svg-icons";
import type { Ref } from "vue";
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import NotificationBell from "./NotificationBell.vue";
import PermissionRequestModal from "./PermissionRequestModal.vue";

const me = ref<any>({});
const isAdmin = computed(() => me.value.is_admin);
const isDesktop = inject<Ref<boolean>>("isDesktop")!;
const isSidebarOpen = inject<Ref<boolean>>("isSidebarOpen")!;

const showCrumbBell = computed(
	() => !isDesktop.value || (isDesktop.value && !isSidebarOpen.value),
);

const devices = ref<any[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const router = useRouter();
const searchQuery = ref("");
const currentPage = ref(1);
const itemsPerPage = 10;
const currentPageInput = ref(1);
const toast = useToast();

const sortBy = ref("updatedAt");
const sortOrder = ref<"asc" | "desc">("desc");

const toggleSort = (key: string) => {
	if (sortBy.value === key) {
		sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
	} else {
		sortBy.value = key;
		sortOrder.value = "asc";
	}
};

watch(currentPage, (val) => {
	currentPageInput.value = val;
});

const selectedIds = ref<number[]>([]);

const isAllSelected = computed(() => {
	if (paginatedDevices.value.length === 0) return false;
	return paginatedDevices.value.every((d) => selectedIds.value.includes(d.id));
});

function toggleSelectAll() {
	if (isAllSelected.value) {
		const pageIds = paginatedDevices.value.map((d) => d.id);
		selectedIds.value = selectedIds.value.filter((id) => !pageIds.includes(id));
	} else {
		paginatedDevices.value.forEach((d) => {
			if (!selectedIds.value.includes(d.id)) {
				selectedIds.value.push(d.id);
			}
		});
	}
}

const jumpToPage = () => {
	let p = parseInt(String(currentPageInput.value));
	if (isNaN(p)) {
		currentPageInput.value = currentPage.value;
		return;
	}
	if (p < 1) p = 1;
	if (p > totalPages.value) p = totalPages.value;
	currentPage.value = p;
	currentPageInput.value = p;
};

const showRenameModal = ref(false);
const tempName = ref("");
const selectedDevice = ref<any | null>(null);

const showArchiveModal = ref(false);
const isBulkArchive = ref(false);
const archiveLoading = ref(false);

const showHistoryModal = ref(false);
const historyLoading = ref(false);
const deviceHistory = ref<any[]>([]);

const showStatusModal = ref(false);
const tempStatus = ref<string | null>(null);
const statusLoading = ref(false);

// Permission Modal State
const showPermissionModal = ref(false);
const permissionDevice = ref<any | null>(null);
const failedPermissionType = ref<
	"change_status" | "change_mode" | "change_thresholds"
>("change_status");

// Editor Menu State
const openEditorMenuId = ref<string | number | null>(null);

// New Device Modal State
const showNewDeviceModal = ref(false);
const newDeviceStep = ref(1);
const newDeviceForm = ref({
	email: "",
	otp: "",
	name: "",
	type: "static" as "static" | "dynamic",
	modules: [] as string[],
	lat: "",
	lng: "",
});
const otpRequestLoading = ref(false);
const otpVerifyLoading = ref(false);
const isOtpSent = ref(false);

const filteredDevices = computed(() => {
	if (!searchQuery.value) return devices.value;
	const q = searchQuery.value.toLowerCase();
	return devices.value.filter((d) => {
		return (
			d.id.toString().toLowerCase().includes(q) ||
			(d.name && d.name.toLowerCase().includes(q)) ||
			(d.status && d.status.toLowerCase().includes(q))
		);
	});
});

const sortedDevices = computed(() => {
	const result = [...filteredDevices.value];
	return result.sort((a, b) => {
		let valA = a[sortBy.value];
		let valB = b[sortBy.value];

		if (sortBy.value === "updatedAt") {
			valA = valA ? new Date(valA).getTime() : 0;
			valB = valB ? new Date(valB).getTime() : 0;
		}

		if (valA < valB) return sortOrder.value === "asc" ? -1 : 1;
		if (valA > valB) return sortOrder.value === "asc" ? 1 : -1;
		return 0;
	});
});

const paginatedDevices = computed(() => {
	const start = (currentPage.value - 1) * itemsPerPage;
	return sortedDevices.value.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => {
	return Math.ceil(filteredDevices.value.length / itemsPerPage);
});

watch(searchQuery, () => {
	currentPage.value = 1;
});

/* ref al contenitore scrollabile + stato del bottone */
const historyContainer = ref<HTMLElement | null>(null);
const showScrollTop = ref(false);

// Opzioni di stato con etichette
const statusOptions = [
	{ value: "online", label: "Online", class: "on" },
	{ value: "offline", label: "Offline", class: "off" },
	{ value: "maintenance", label: "Manutenzione", class: "maintenance" },
	{ value: "error", label: "Errore", class: "error" },
];

/* -------------------------------------------------- */
/*  FILTRI CRONOLOGIA                                 */
/* -------------------------------------------------- */
const filters = [
	{ key: "status", label: "Cambio stato" },
	{ key: "connection", label: "Connessione aggiunta/rimossa" },
	{ key: "node_moved", label: "Modulo spostato" },
	{ key: "node_added", label: "Modulo aggiunto" },
];

const activeFilters = ref<string[]>([]);

async function goToEditorSchema() {
	closeEditorMenu();

	const API_BASE = (API_URL || "").replace(/\/$/, ""); // http://localhost:3000/api

	//    const editorBase = new URL("https://d12f-151-74-127-120.ngrok-free.app");
	const editorBase = new URL(
		EDITOR_BASE_URL || "https://d12f-151-74-127-120.ngrok-free.app",
	);
	console.log("editorBase =", editorBase);
	console.log("EDITOR_BASE_URL =", EDITOR_BASE_URL);

	const fakeDraftId = "1afcf8f6-a1b2-4665-bf1e-7e7799095864";

	const getTicket = async () => {
		const ticket = await axios.get(
			`${API_BASE}/auth/editor/ticket?returnTo=${editorBase}draft/${fakeDraftId}`,
		);
		console.log("ticket =", ticket);
		return ticket.data.ticketId;
	};

	const ticket = await getTicket();

	console.log("ticket =", ticket);

	//    const EDITOR_SCHEMA_URL =
	// 	//  "https://staging-editor.archimede.world/draft/1afcf8f6-a1b2-4665-bf1e-7e7799095864/9d2a8a00-32b2-473c-a5f8-2e113fc86f7b";
	// 	 `https://e8e2-151-74-127-120.ngrok-free.app/sso/
	// ${ticket} `

	// console.log("EDITOR_SCHEMA_URL =", EDITOR_SCHEMA_URL);

	const editorSchemaUrl = new URL(`/sso/${ticket}`, editorBase).toString();
	const startUrl = `${API_BASE}/auth/editor/start?returnTo=${encodeURIComponent(
		editorSchemaUrl,
	)}`;
	window.location.assign(startUrl);
}

function ownerDisplayName(d: any) {
	const u = d.user || {};
	// scegli l’ordine che preferisci: name → username → email → fallback
	return u.name || u.username || u.email || "utente";
}

function openStatusModal(device: any) {
	selectedDevice.value = device;
	tempStatus.value = device.status;
	showStatusModal.value = true;
}

function selectStatus(status: string) {
	tempStatus.value = status;
}

function getStatusClass(status: string): string {
	const statusClasses: Record<string, string> = {
		online: "on",
		offline: "off",
		maintenance: "maintenance",
		error: "error",
	};
	return statusClasses[status] || "off";
}

async function confirmStatusChange() {
	if (!selectedDevice.value || !tempStatus.value) return;

	try {
		statusLoading.value = true;
		const response = await axios.patch(
			`/devices/${selectedDevice.value.id}/change-status`,
			{ status: tempStatus.value },
			{ withCredentials: true },
		);

		// Aggiorna lo stato localmente
		selectedDevice.value.status = tempStatus.value;
		selectedDevice.value.updatedAt = response.data.updatedAt;

		toast.success(
			`Stato del dispositivo ${selectedDevice.value.name} cambiato a ${tempStatus.value}`,
			{
				position: "top-right",
				duration: 2000,
			},
		);

		showStatusModal.value = false;
	} catch (e: any) {
		// Chiudi il modale prima di mostrare l'errore
		showStatusModal.value = false;

		// Check for permission error (401 or specific message)
		const isPermissionError =
			e.response?.status === 401 ||
			(e.response?.data?.message &&
				e.response.data.message.includes("Non hai i permessi"));

		if (isPermissionError) {
			// Open Permission Request Modal
			permissionDevice.value = selectedDevice.value; // keep reference
			failedPermissionType.value = "change_status";

			// Show toast - extract full message from backend
			let msg = "Non hai i permessi";
			const responseData = e.response?.data;
			if (typeof responseData === "string") {
				msg = responseData;
			} else if (responseData?.message) {
				msg = responseData.message;
			} else if (responseData?.details) {
				msg = responseData.details;
			} else if (responseData?.error) {
				msg = responseData.error;
			}

			toast.error(msg, { position: "top-right", duration: 4000 });

			// Delay modal to let user read toast
			setTimeout(() => {
				showPermissionModal.value = true;
			}, 2500);
			return;
		}

		// Prioritize backend message: e.response.data.message or e.response.data
		const msg =
			e.response?.data?.message ||
			e.response?.data?.details ||
			e.response?.data ||
			"Errore cambio stato";
		const finalMsg = typeof msg === "object" ? JSON.stringify(msg) : msg;

		// Mostra il toast con un leggero ritardo
		setTimeout(() => {
			toast.error(finalMsg, { position: "top-right", duration: 4000 });
		}, 300);
	} finally {
		statusLoading.value = false;
	}
}

function cancelStatusChange() {
	showStatusModal.value = false;
	tempStatus.value = null;
	selectedDevice.value = null;
}

function openRenameModal(device: any) {
	selectedDevice.value = device;
	tempName.value = device.name || "";
	showRenameModal.value = true;
}

async function confirmRename() {
	const name = tempName.value.trim();
	if (!name || !selectedDevice.value) return;

	try {
		const response = await axios.post(
			`/devices/${selectedDevice.value.id}/rename`,
			{ name },
			{ withCredentials: true },
		);
		selectedDevice.value.name = response.data.name;
		selectedDevice.value.updatedAt = response.data.updatedAt;
		toast.success(`Nome del device ${selectedDevice.value.id} aggiornato!`, {
			position: "top-right",
			duration: 2000,
		});
		cancelRename();
	} catch (e: any) {
		const msg = e.response?.data?.message || "Errore rinomina";
		toast.error(msg, { position: "top-right", duration: 2500 });
	}
}

function cancelRename() {
	showRenameModal.value = false;
	tempName.value = "";
	selectedDevice.value = null;
}

function openArchiveModal(device: any) {
	selectedDevice.value = device;
	isBulkArchive.value = false;
	showArchiveModal.value = true;
}

function openBulkArchiveModal() {
	isBulkArchive.value = true;
	if (selectedIds.value.length === 1) {
		selectedDevice.value = devices.value.find(
			(d) => d.id === selectedIds.value[0],
		);
	}
	showArchiveModal.value = true;
}

async function confirmArchive() {
	if (isBulkArchive.value) {
		await confirmBulkArchive();
		return;
	}
	if (!selectedDevice.value) return;

	archiveLoading.value = true;
	try {
		await axios.post(
			`/devices/${selectedDevice.value.id}/archive`,
			{},
			{ withCredentials: true },
		);
		devices.value = devices.value.filter(
			(d) => d.id !== selectedDevice.value.id,
		);
		toast.success(`Device con id: ${selectedDevice.value.id} archiviato`, {
			position: "top-right",
			duration: 2000,
		});
		cancelArchive();
	} catch (e: any) {
		const msg = e.response?.data?.message || "Errore archiviazione";
		toast.error(msg, { position: "top-right", duration: 2500 });
	} finally {
		archiveLoading.value = false;
	}
}

async function confirmBulkArchive() {
	archiveLoading.value = true;
	let successCount = 0;
	let errorCount = 0;

	for (const id of selectedIds.value) {
		try {
			await axios.post(`/devices/${id}/archive`, {}, { withCredentials: true });
			successCount++;
		} catch (e) {
			errorCount++;
		}
	}

	if (successCount > 0) {
		devices.value = devices.value.filter(
			(d) => !selectedIds.value.includes(d.id),
		);
		toast.success(
			`${
				selectedIds.value.length > 1 ? "Dispositivi" : "Dispositivo"
			} archiviat${selectedIds.value.length > 1 ? "i" : "o"} con successo`,
			{
				position: "top-right",
				duration: 3000,
			},
		);
	}

	if (errorCount > 0) {
		toast.error(`Errore durante l'archiviazione di ${errorCount} dispositivi`, {
			position: "top-right",
			duration: 3000,
		});
	}

	selectedIds.value = [];
	cancelArchive();
	archiveLoading.value = false;
}

function cancelArchive() {
	showArchiveModal.value = false;
	isBulkArchive.value = false;
	selectedDevice.value = null;
}

/* mostra / nasconde il bottone in base allo scroll */
function onScroll() {
	if (!historyContainer.value) return;
	showScrollTop.value = historyContainer.value.scrollTop > 300; // soglia px
}

/* scorri dolcemente fino in cima */
function scrollToTop() {
	historyContainer.value?.scrollTo({ top: 0, behavior: "smooth" });
}

function parseDetails(raw: any) {
	if (raw == null) return {};
	// se è già un oggetto lo restituisco subito
	if (typeof raw === "object") return raw;
	// altrimenti provo a fare il parse
	try {
		return JSON.parse(raw as string);
	} catch {
		console.warn("Dettagli non parsabili:", raw);
		return {};
	}
}

const processedHistory = computed(() => {
	const raw = [...deviceHistory.value]; // copia per non mutare
	const result: any[] = [];

	for (let i = 0; i < raw.length; i++) {
		const cur = raw[i];
		const d = parseDetails(cur.details);

		/* ───── STATUS ───── */
		if (cur.type === "status" || cur.eventType === "status_change") {
			result.push({
				...cur,
				type: "status",
				filterKey: "status",
				details: {
					oldStatus: d.oldStatus ?? d.old_status,
					newStatus: d.newStatus ?? d.new_status,
				},
			});
			continue;
		}

		/* ───── CONNESSIONI ───── */
		if (cur.type === "connection") {
			result.push({
				...cur,
				filterKey: "connection",
				details: {
					...cur.details,
					display_source:
						cur.details.source_label || cur.details.source_node_id || "N/A",
					display_target:
						cur.details.target_label || cur.details.target_node_id || "N/A",
					display_output:
						cur.details.source_output === "value"
							? "—"
							: cur.details.source_output,
					display_input:
						cur.details.target_input === "value"
							? "—"
							: cur.details.target_input,
				},
			});
			continue;
		}

		/* ───── NODI (add / move) ───── */
		if (cur.type === "node") {
			const base = {
				...cur,
				type: "node",
				details: {
					...cur.details,
					x: Math.round(cur.details.x),
					y: Math.round(cur.details.y),
				},
			};

			if (cur.type === "node" && cur.eventType === "modified") {
				// se cambiano solo x,y e non altri campi
				const { old, new: nu } = parseDetails(cur.details);
				if (old.x !== nu.x || old.y !== nu.y) {
					result.push({
						...cur,
						filterKey: "node_moved",
						processedType: "moved",
						oldPosition: { x: Math.round(old.x), y: Math.round(old.y) },
						details: {
							x: Math.round(nu.x),
							y: Math.round(nu.y),
							label: nu.label,
						},
					});
					continue;
				}
			}

			/* “added + removed” → spostamento */
			if (cur.eventType === "added") {
				const removedIdx = raw.findIndex(
					(e) =>
						e.type === "node" &&
						e.eventType === "removed" &&
						e.nodeId === cur.nodeId,
				);

				if (removedIdx !== -1) {
					const removedEvt = raw[removedIdx];
					result.push({
						...base,
						filterKey: "node_moved",
						processedType: "moved",
						oldPosition: {
							x: Math.round(removedEvt.details.x),
							y: Math.round(removedEvt.details.y),
						},
					});
					raw.splice(removedIdx, 1); // non serve più il “removed”
					continue;
				}
			}

			/* semplice “added” */
			if (cur.eventType === "added") {
				result.push({ ...base, filterKey: "node_added" });
				continue;
			}

			/* tutto il resto resta “node” generico (se ti serve) */
			result.push({ ...base, filterKey: "node_generic" });
			continue;
		}

		/* fallback: evento sconosciuto */
		result.push({ ...cur, filterKey: "other" });
	}

	/* ordine cronologico desc. */
	return result.sort(
		(a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
	);
});

/** ➜ Applica i checkbox: mostra SOLO gli eventi il cui filterKey è selezionato */
const filteredHistory = computed(() => {
	if (!activeFilters.value.length) return processedHistory.value;

	const active = new Set(activeFilters.value);
	return processedHistory.value.filter((ev) => active.has(ev.filterKey));
});

// function goToRecords() {
//   if (!selectedDevice.value) return;

//   router.push({
//     name: "Records",
//     params: { deviceId: selectedDevice.value.id },
//   });
// }

async function loadDeviceHistory(deviceId: any) {
	historyLoading.value = true;
	try {
		const { data } = await axios.get(`${API_URL}/devices/${deviceId}/history`);
		deviceHistory.value = [
			...(data.statusChanges || []),
			...(data.nodeChanges || []),
			...(data.connectionChanges || []),
		].sort(
			(a, b) =>
				new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
		);
		// console.log("device history", deviceHistory.value);
	} catch (error) {
		toast.error(
			`Errore nel caricamento della cronologia del device con id: ${deviceId}`,
			{
				position: "top-right",
				duration: 2500,
			},
		);
	} finally {
		historyLoading.value = false;
	}
}

function getEventTypeLabel(event: { type: string }) {
	const types: Record<string, string> = {
		status: "Stato",
		node: "Modulo",
		connection: "Connessione",
	};
	return types[event.type] || "Evento";
}

function getActionLabel(event: any): string {
	// console.log("Event", event);
	if (event.processedType === "moved") return "Spostato";
	if (event.processedType === "reconnected") return "Riconfigurata";

	const actions: Record<string, string> = {
		added: "Aggiunto",
		removed: "Rimosso",
		modified: "Modificato",
		status_change: "Cambiato",
	};
	// Aggiungi suffissi di genere in base al type
	const baseAction =
		actions[event.eventType] || event.eventType || "Modificato";

	if (event.type === "connection") {
		return baseAction.replace(/o$/, "a"); // Trasforma in femminile
	}

	return baseAction;
}

async function fetchMe() {
	try {
		const { data } = await axios.get("/auth/me", { withCredentials: true });
		me.value = data; // <-- popola isAdmin
	} catch {
		me.value = {};
	}
}

async function loadDevices() {
	loading.value = true;
	try {
		const params: any = {};
		if (isAdmin.value) params.all = true; // per admin => vede tutti i dispositivi sia suoi che degli altri utenti

		const { data, status } = await axios.get("/devices/get-all-graphs", {
			params,
			withCredentials: true,
		});

		// Gestione esplicita dello status 204 => quando non ci sono dispositivi
		if (status === 204) {
			devices.value = [];
			toast.info("Nessun dispositivo trovato", {
				position: "top-right",
				duration: 2000,
			});
			return;
		}

		// Filtra i dispositivi non archiviati
		devices.value = (data ?? []).filter((g: any) => g.is_archived === false);

		if (devices.value.length === 0) {
			toast.info("Nessun dispositivo attivo trovato", {
				position: "top-right",
				duration: 2000,
			});
		}
	} catch (e: any) {
		error.value = e.response?.data?.details || "Errore caricamento";
		toast.error("Errore nel caricamento dei dispositivi", {
			position: "top-right",
			duration: 2000,
		});
	} finally {
		loading.value = false;
	}
}

function openNewDeviceModal() {
	newDeviceStep.value = 1;
	newDeviceForm.value = {
		email: "",
		otp: "",
		name: "",
		type: "static",
		modules: [],
		lat: "",
		lng: "",
	};
	isOtpSent.value = false;
	showNewDeviceModal.value = true;
}

async function requestOtp() {
	if (!newDeviceForm.value.email) return;

	otpRequestLoading.value = true;
	try {
		await axios.post(`${API_URL}/devices/device/request-otp`, {
			email: newDeviceForm.value.email,
		});

		toast.success("Codice OTP inviato con successo! Controlla la tua email.", {
			position: "top-right",
			duration: 3000,
		});
		isOtpSent.value = true;
	} catch (e: any) {
		const msg = e.response?.data?.message || "Errore invio OTP";
		toast.error(msg, { position: "top-right", duration: 3000 });
	} finally {
		otpRequestLoading.value = false;
	}
}

async function nextNewDeviceStep() {
	if (newDeviceStep.value === 1) {
		if (!isOtpSent.value) {
			toast.warning("Devi prima richiedere il codice OTP", {
				position: "top-right",
			});
			return;
		}

		if (!newDeviceForm.value.otp) {
			toast.warning("Inserisci il codice OTP", { position: "top-right" });
			return;
		}

		otpVerifyLoading.value = true;
		try {
			await axios.post(`${API_URL}/devices/device/verify-otp`, {
				otpCode: newDeviceForm.value.otp,
			});

			// Verify success -> Proceed to Step 2

			// Genera un nome randomico se vuoto
			if (!newDeviceForm.value.name) {
				const adjectives = [
					"Smart",
					"Quick",
					"Bright",
					"Swift",
					"Active",
					"Dynamic",
					"Rapid",
					"Agile",
				];
				const nouns = [
					"Device",
					"Sensor",
					"Monitor",
					"Tracker",
					"Unit",
					"Node",
					"Station",
					"Hub",
				];
				const randomAdj =
					adjectives[Math.floor(Math.random() * adjectives.length)];
				const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
				const randomNum = Math.floor(Math.random() * 1000);
				newDeviceForm.value.name = `${randomAdj}-${randomNoun}-${randomNum}`;
			}

			newDeviceStep.value = 2;
		} catch (e: any) {
			const msg =
				e.response?.data?.message || "Codice OTP non valido o scaduto";
			toast.error(msg, { position: "top-right", duration: 3000 });
		} finally {
			otpVerifyLoading.value = false;
		}
	}
}

async function confirmNewDevice() {
	// Logica di creazione (per ora simulata come richiesto)
	console.log("Creazione device con dati:", newDeviceForm.value);

	try {
		// In futuro qui andrà la chiamata reale
		const lat = parseFloat(newDeviceForm.value.lat);
		const lng = parseFloat(newDeviceForm.value.lng);
		const hasLocation = !isNaN(lat) && !isNaN(lng);

		const response = await axios.post(
			`${API_URL}/devices/device/complete-creation`,
			{
				name: newDeviceForm.value.name,
				is_static: newDeviceForm.value.type === "static",
				has_sat: newDeviceForm.value.modules.includes("sat"),
				has_lora: newDeviceForm.value.modules.includes("lora"),
				has_lte: newDeviceForm.value.modules.includes("lte"),
				has_plc: newDeviceForm.value.modules.includes("plc"),
				...(hasLocation && { location: [lat, lng] }),
			},
		);

		toast.success(
			`Nuovo Device con id: ${response.data.id} aggiunto con successo`,
			{
				position: "top-right",
				duration: 2000,
			},
		);

		showNewDeviceModal.value = false;
		await loadDevices();
	} catch (e: any) {
		const msg = e.response?.data?.message || "Errore creazione device";
		toast.error(msg, { position: "top-right", duration: 3000 });
	}
}

async function newDevice() {
	openNewDeviceModal();
}

function formatDate(iso: string) {
	return dayjs(iso).format("DD/MM/YYYY HH:mm");
}

function formatTime(iso: string) {
	return dayjs(iso).format("HH:mm");
}

function listModules(d: any) {
	return d.nodes?.map((n: any) => n.label).join(", ") || "—";
}

function rename(device: any) {
	openRenameModal(device);
}

function openData(d: any) {
	router.push({ name: "Data", params: { deviceId: d.id } });
}

function edit(d: any) {
	router.push({ name: "Editor", params: { deviceId: d.id } });
	closeEditorMenu();
}

function openSchema(_d: any) {
	goToEditorSchema();
}

function toggleEditorMenu(id: string | number) {
	if (openEditorMenuId.value === id) {
		openEditorMenuId.value = null;
	} else {
		openEditorMenuId.value = id;
	}
}

function closeEditorMenu() {
	openEditorMenuId.value = null;
}

function handleClickOutside(event: MouseEvent) {
	const target = event.target as HTMLElement;
	if (!target.closest(".editor-menu-wrapper")) {
		closeEditorMenu();
	}
}

async function log(d: any) {
	selectedDevice.value = d;
	showHistoryModal.value = true;
	await loadDeviceHistory(d.id);
}

function del(device: any) {
	openArchiveModal(device);
}

/*  ▸  quando apro/chiudo la history-modal
       – su viewport < 1023 px aggiungo/tolgo una
         classe al <body> che fa “sparire” la sidebar       */
watch(showHistoryModal, (open: any) => {
	if (window.innerWidth < 1023) {
		document.body.classList.toggle("hist-open", open);
	}
});

onMounted(async () => {
	document.addEventListener("click", handleClickOutside);
	await fetchMe();
	await loadDevices();
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
});

window.addEventListener("graph-restored", async () => {
	await loadDevices();
});
</script>

<style scoped>
section {
	padding-bottom: 80px;
}
.owner-badge {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 2px 8px;
	border-radius: 10px;
	font: 600 12px/1 Inter, sans-serif;
	border: 1px solid #99a;
	color: #334;
	background: #eef6;
}
.owner-badge.admin {
	border-color: #008f8f;
	color: #008f8f;
	background: color-mix(in srgb, #008f8f 8%, white);
}
.owner-role {
	text-transform: uppercase;
	letter-spacing: 0.3px;
}
.owner-sep {
	opacity: 0.5;
}
.owner-id {
	opacity: 0.7;
	font-weight: 500;
}
/* ───── modali ───── */
.modal-mask {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.55);
	backdrop-filter: blur(3px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 999;
}

.modal {
	min-width: 340px;
	max-width: 90%;
	background: #0d0d0d;
	color: #fff;
	padding: 28px 32px;
	border-radius: 8px;
	box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
	animation: pop 0.25s ease-out;
}

@keyframes pop {
	from {
		opacity: 0;
		transform: scale(0.92);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.history-modal {
	max-width: 800px;
	max-height: 80vh;
	overflow-y: auto;
	padding: 28px 32px;
	scrollbar-width: none;
	-ms-overflow-style: none;
}

.history-modal::-webkit-scrollbar {
	display: none;
	width: 0;
	height: 0;
}

/* colore titoli “Informazioni Base” e “Eventi” */
.history-section h4 {
	color: #00b7b7;
}

/* ───── pulsante “torna su” ───── */
/* wrapper che resta appiccicato al fondo del contenitore scrollabile (.history-modal) */
.scroll-top-wrap {
	position: sticky;
	bottom: 16px; /* distanza dal bordo inferiore del MODALE */
	z-index: 3; /* sopra al contenuto del modale (header è z=2) */
	display: flex;
	justify-content: center; /* centra orizzontalmente il bottone */
	pointer-events: none; /* il wrapper non intercetta click */
}

/* il bottone torna “static” e riabilita i click */
.scroll-top-wrap .scroll-top-btn {
	position: static;
	pointer-events: auto;
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	background: #008f8f;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s, transform 0.15s;
}

.scroll-top-wrap .scroll-top-btn:hover {
	background: #00a7a7;
}
.scroll-top-wrap .scroll-top-btn:active {
	transform: scale(0.92);
}
.scroll-top-wrap .scroll-top-btn svg {
	width: 22px;
	height: 22px;
	stroke: #fff;
}

.modal-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid #444;
	border-radius: 5px;
	background: #1a1a1a;
	color: #eee;
	transition: border 0.2s;
}

.modal-input:focus {
	outline: none;
	border-color: #008f8f;
}

.modal-actions {
	margin-top: 26px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
}

.modal-actions button {
	min-width: 88px;
	padding: 8px 16px;
	font: 600 14px/1 "Inter", sans-serif;
	border-radius: 4px;
	cursor: pointer;
	color: #fff;
	background: #008f8f;
	border: 1px solid #008f8f;
	transition: background 0.15s;
}

.modal-actions button:hover {
	background: #00a7a7;
}

.modal-actions button:active {
	background: #007777;
}

.modal.history-modal {
	position: relative; /* serve per lo sticky */
	display: flex;
	flex-direction: column;
}

.modal-header {
	position: sticky;
	top: 0;
	z-index: 2;
	display: flex;
	justify-content: space-between;
	align-items: center;

	/* padding identico alla .modal */
	padding: 28px 0 8px;
	margin-bottom: 18px; /* solo distacco dal contenuto */
	background: #0d0d0d;
	border-bottom: 1px solid #444;
}

.modal-close {
	background: none;
	border: 0;
	cursor: pointer;
	padding: 4px;
}

.modal-close svg {
	width: 20px;
	height: 20px;
	stroke: #fff;
	transition: stroke 0.15s;
}

.modal-close:hover svg {
	stroke: #008f8f;
}

/* --- filtri cronologia --- */
.filters {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	margin: 8px 0 16px;
}

/* ▸ CHECKBOX FILTRI — restyling */
.filter-item input {
	/* nascondiamo il checkbox nativo */
	-webkit-appearance: none;
	appearance: none;

	/* box personalizzato */
	width: 16px;
	height: 16px;
	border: 2px solid #008f8f;
	border-radius: 4px;
	background: #fff;
	cursor: pointer;
	position: relative;
	transition: background-color 0.15s, border-color 0.15s;
}

/* stato hover / focus */
.filter-item input:hover {
	border-color: #00a7a7;
}
.filter-item input:focus-visible {
	outline: 2px solid #00b7b7;
	outline-offset: 2px;
}

/* stato checked */
.filter-item input:checked {
	background: #008f8f;
	border-color: #008f8f;
}

/* ✓ bianco al centro */
.filter-item input:checked::after {
	content: "✔";
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -62%);
	font-size: 12px;
	color: #fff;
	line-height: 1;
}

.filter-item {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	cursor: pointer;
}

.filters-reset {
	background: none;
	border: 0;
	color: #008f8f;
	cursor: pointer;
	font-size: 14px;
	padding: 0 8px;
}

.filters-reset:hover {
	text-decoration: underline;
}

.history-content {
	margin: 20px 0;
}

.history-section {
	margin-bottom: 20px;
	padding-bottom: 15px;
	border-bottom: 1px solid #444;
}

.info-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 8px;
	/* margin-top: 12px; */
}

.info-item {
	display: flex;
	gap: 1rem;
}

.info-value {
	margin-left: auto; /* spinge il gruppo a destra */
	display: inline-flex;
	align-items: center;
	gap: 0.5rem; /* spazio pallino-testo */
}

.info-label {
	font-weight: 600;
	min-width: 160px;
	color: #ccc;
}

/* .status-btn {
		background: none;
		border: none;
		cursor: pointer;
		padding: 4px;
		border-radius: 50%;
		transition: background-color 0.2s;
	}

	.status-btn:hover {
		background-color: rgba(0, 0, 0, 0.1);
	} */

/* ───────── bottone stato ───────── */
.status-btn {
	display: inline-flex; /* pallino + icona allineati orizzontalmente */
	align-items: center;
	gap: 6px; /* spazio fra pallino e icona */
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	border-radius: 4px;
	transition: background-color 0.2s;
}

.status-btn:hover {
	background-color: rgba(0, 0, 0, 0.06);
}

/* icona matita */
.status-edit-icon {
	width: 14px;
	height: 14px;
	stroke: #666;
	opacity: 0; /* invisibile di default…  */
	transition: opacity 0.15s, stroke 0.15s;
}

/* …ma visibile quando il mouse è sulla cella */
.status-btn:hover .status-edit-icon {
	opacity: 1;
}

/* cambio colore se il mouse va proprio sull’icona */
.status-btn:hover .status-edit-icon:hover {
	stroke: #008f8f;
}

.status-dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	margin-right: 0.5rem; /* spazio tra dot e testo */
	box-shadow: 0 0 0 1px #0d0d0d;
	vertical-align: middle;
}

.status-options {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	margin-bottom: 20px;
}

.status-options button {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px;
	border: 1px solid #444;
	border-radius: 4px;
	background: #1a1a1a;
	color: #eee;
	cursor: pointer;
	transition: all 0.2s;
}

.status-options button:hover {
	background: #252525;
}

.status-options button.active {
	border-color: #008f8f;
	background: rgba(0, 143, 143, 0.2);
}

.status-options button span {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
}

.status-badge {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 2px 8px;
	border-radius: 12px;
	font-weight: 500;
	background-color: rgba(0, 143, 143, 0.15);
}

.status-badge.on {
	color: #27ae60;
}

.status-badge.off {
	color: #e74c3c;
}

.status-badge.maintenance {
	color: #f39c12;
}

.status-badge.error {
	color: #9b59b6;
}

.events-list {
	margin-top: 16px;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.event-item {
	display: flex;
	gap: 12px;
	padding: 12px;
	background-color: #1a1a1a;
	border-radius: 6px;
	border-left: 4px solid;
	transition: background-color 0.2s;
}

.event-item:hover {
	background-color: #252525;
}

.event-item.status {
	border-left-color: #27ae60;
}

.event-item.node {
	border-left-color: #2196f3;
}

.event-item.connection {
	border-left-color: #ff9800;
}

.event-item.moved {
	border-left-color: #9c27b0;
	background-color: rgba(156, 39, 176, 0.1);
}

.event-item.reconnected {
	border-left-color: #ff9800;
	background-color: rgba(255, 152, 0, 0.08);
}

.event-item.connection.added {
	border-left-color: #4caf50;
	background-color: rgba(76, 175, 80, 0.08);
}

.event-item.connection.removed {
	border-left-color: #f44336;
	background-color: rgba(244, 67, 54, 0.08);
}

.event-time {
	font-size: 13px;
	color: #999;
	min-width: 50px;
	padding-top: 2px;
}

.event-content {
	flex: 1;
}

.event-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 6px;
}

.event-type {
	font-weight: 600;
	color: #008f8f;
	text-transform: capitalize;
}

.event-action {
	font-size: 13px;
	color: #bbb;
	text-transform: capitalize;
}

.event-details {
	font-size: 14px;
	line-height: 1.5;
}

.status-value {
	font-weight: 500;
	padding: 2px 6px;
	border-radius: 4px;
}

.status-value.on {
	background-color: rgba(39, 174, 96, 0.2);
	color: #27ae60;
}

.status-value.off {
	background-color: rgba(231, 76, 60, 0.2);
	color: #e74c3c;
}

.status-value.maintenance {
	background-color: rgba(243, 156, 18, 0.2);
	color: #f39c12;
}

.status-value.error {
	background-color: rgba(155, 89, 182, 0.2);
	color: #9b59b6;
}

.connection-detail {
	font-family: monospace;
	background-color: rgba(0, 0, 0, 0.1);
	padding: 2px 4px;
	border-radius: 3px;
}

.breadcrumb-actions {
	display: flex;
	align-items: center;
	gap: 12px; /* distanza tra i due bottoni */
}

.breadcrumb {
	position: sticky;
	top: 0;
	z-index: 10;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 24px;
	background: #0d0d0d;
	font: 600 18px/56px "Inter", sans-serif;
	border-bottom: 2px solid transparent;
	border-image: linear-gradient(to bottom, #535454, #2d3e4f) 1;

	--bc-bg: #0d0d0d;
	--bc-fg: #e6e6e6;
	--bc-hover: color-mix(in srgb, white 8%, var(--bc-bg)); /* overlay 8% */
	--bc-active: color-mix(in srgb, black 35%, var(--bc-bg)); /* click */
	--bc-border: color-mix(in srgb, white 12%, var(--bc-bg));
}

.btn-new {
	padding: 8px 16px;
	background: #008f8f;
	border: 0;
	color: #fff;
	border-radius: 4px;
	cursor: pointer;
	font: 600 14px/1 "Inter", sans-serif;
	transition: background 0.15s;
}

.btn-new:hover {
	background: #00a7a7;
}

.btn-new:active {
	background: #007777;
}

.msg {
	margin: 24px 0;
	color: #666;
	text-align: center;
	padding: 16px;
	background: #f5f5f5;
	border-radius: 4px;
}

.error {
	color: #d00;
}

.dashboard-wrapper {
	display: flex;
	flex-direction: column;
	/* height: 100%; */
	min-height: 100vh;
	padding: 16px;
	box-sizing: border-box;
	padding-bottom: 24px;
}

.tbl-wrap {
	margin-top: 50px;
	border: 1px solid #ddd;
	border-radius: 4px;
	overflow-x: auto;
	/* border-top: none; */
}

.tbl {
	width: 100%;
	border-collapse: collapse;
	font: 14px "Inter", sans-serif;
}

th,
td {
	padding: 10px 16px;
	text-align: left;
	white-space: nowrap;
}

.header-with-action {
	display: flex;
	align-items: center;
	gap: 8px;
}

.btn-bulk-action {
	background: none;
	border: none;
	padding: 4px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	transition: background 0.2s;
}

.btn-bulk-action svg {
	width: 18px;
	height: 18px;
	stroke: #444;
	transition: 0.2s;
}

.btn-bulk-action:hover svg,
.btn-bulk-action:focus svg {
	stroke: #e74c3c;
	transform: scale(1.1);
}

.actions-header-bulk {
	padding: 0 16px;
	text-align: right;
}

.actions-header-bulk button {
	padding: 0 5px;
}

/* Gmail-style Selection Bar */
.selection-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: #fff;
	border: 1px solid #008f8f;
	border-radius: 8px;
	padding: 8px 16px;
	margin-bottom: 0px;
	box-shadow: 0 4px 12px rgba(0, 143, 143, 0.15);
	animation: slideDown 0.3s ease-out;
}

.selection-info {
	display: flex;
	align-items: center;
	gap: 12px;
	font-weight: 600;
	color: #333;
}

.btn-close-selection {
	background: none;
	border: none;
	cursor: pointer;
	padding: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	transition: background 0.2s;
}

.btn-close-selection:hover {
	background: #f0f0f0;
}

.btn-close-selection svg {
	width: 18px;
	height: 18px;
	stroke: #666;
}

.selection-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.btn-selection-action {
	display: flex;
	align-items: center;
	gap: 8px;
	background: #fff;
	border: 1px solid #dee2e6;
	padding: 8px 16px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 0.9rem;
	font-weight: 600;
	color: #444;
	transition: all 0.2s;
}

.btn-selection-action:hover {
	background-color: #f8f9fa;
	border-color: #e74c3c;
	color: #e74c3c;
	transform: translateY(-1px);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-selection-action svg {
	width: 18px;
	height: 18px;
	stroke: currentColor;
}

/* Transitions */
.slide-fade-enter-active {
	transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
	transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateY(-20px);
	opacity: 0;
}

@keyframes slideDown {
	from {
		transform: translateY(-10px);
		opacity: 0;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

.select-col {
	width: 40px;
	text-align: center;
	padding-right: 0;
}

.select-col input {
	cursor: pointer;
	width: 16px;
	height: 16px;
}

thead {
	font-weight: 600;
	padding: 0;
	margin: 0;
}

.sortable {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s;
}

.sortable:hover {
	background-color: #f0f0f0;
}

.header-content {
	display: flex;
	align-items: center;
	gap: 8px;
}

.sort-icon {
	width: 14px !important;
	height: 14px !important;
	color: #008f8f;
}

/* non funziona */
/* .tbl thead tr:nth-child(odd) {
		background: #ffffff91;
	} */

/* Righe del body alternate */
.tbl tbody tr:nth-child(odd) {
	background: #f5f5f5; /* Grigio chiaro */
}

.tbl tbody tr:nth-child(even) {
	background: #fff; /* Bianco */
}

/* Rimuovi gli stili alternati precedenti se presenti */
.tbl tbody tr.alt {
	background: inherit; /* Resetta lo stile alternato se applicato via classe */
}

.tbl tbody tr:not(.search-row):hover {
	background: #e0e0e0 !important; /* Grigio più scuro al passaggio del mouse */
}

.mono {
	font-family: ui-monospace, monospace;
}

/* ---------- COLORI (riusati da dot, badge, ecc.) ---------- */
.on {
	background: #27ae60;
	color: #27ae60;
}
.off {
	background: #e74c3c;
	color: #e74c3c;
}
.maintenance {
	background: #f39c12;
	color: #f39c12;
	animation: pulse 2s infinite;
}
.error {
	background: #9b59b6;
	color: #9b59b6;
	animation: pulse 1s infinite;
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}

.actions {
	text-align: right;
}

.actions button {
	background: none;
	border: 0;
	cursor: pointer;
	padding: 0px 5px;
}

svg.lucide.lucide-clock-icon.lucide-clock.clock-icon {
	margin: 0 37px 0px 0px;
	cursor: pointer;
}

.clock-icon:hover {
	stroke: #008f8f;
	transform: scale(1.1);
}

.actions svg {
	width: 16px;
	height: 16px;
	stroke: #444;
	transition: 0.2s;
}

.btn-action-labeled {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background-color: #f0f2f5 !important;
	border-radius: 6px;
	padding: 4px 10px !important;
	margin-right: 4px;
	font-size: 0.8rem;
	font-weight: 600;
	color: #444;
	transition: all 0.2s ease;
	border: 1px solid #dee2e6;
}

.btn-action-labeled svg {
	width: 14px;
	height: 14px;
}

.device-cell-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	min-width: 300px;
}

.device-info-left {
	display: flex;
	align-items: center;
	gap: 8px;
}

.device-name-text {
	font-weight: 600;
	color: #333;
}

.device-info-actions {
	display: flex;
	align-items: center;
	gap: 2px;
}

.btn-action-labeled:hover {
	background-color: #008f8f !important;
	color: #fff;
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 143, 143, 0.2);
	border-color: #008f8f;
}

.btn-action-labeled:hover svg {
	stroke: #fff;
}

/* Editor Menu Styles */
.editor-menu-wrapper {
	position: relative;
}

.editor-menu-wrapper .btn-action-labeled {
	padding-right: 6px !important;
}

.chevron-icon {
	width: 14px;
	height: 14px;
	margin-left: -2px;
	transition: transform 0.2s;
}

.dropdown-menu {
	position: absolute;
	top: 100%;
	left: 0;
	z-index: 50;
	background: #1a1a1a;
	border: 1px solid #444;
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
	display: flex;
	flex-direction: column;
	min-width: 110px;
	margin-top: 4px;
	overflow: hidden;
}

.menu-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	background: none;
	border: none;
	color: #eee;
	cursor: pointer;
	text-align: left;
	font-size: 0.9em;
	width: 100%;
	transition: background 0.1s;
}

.menu-item:hover {
	background: #252525;
	color: #008f8f;
}

.actions svg:hover,
.tbl td button.rename-btn svg:hover {
	stroke: #008f8f;
	transform: scale(1.1);
}

.actions button.btn-action-archive svg:hover {
	stroke: #e74c3c;
	transform: scale(1.1);
}

.tbl td button.rename-btn,
.tbl td button.rename-btn svg {
	width: 16px;
	height: 16px;
	stroke: #444;
	transition: 0.2s;
	vertical-align: middle;
	line-height: 1;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Badge per device condivisi */
.shared-device-badge {
	display: inline-block;
	margin: 0 8px;
	padding: 2px 8px;
	font-size: 11px;
	font-weight: 600;
	color: #00838f;
	background: #e0f7fa;
	border: 1px solid #00acc1;
	border-radius: 12px;
	vertical-align: middle;
}

/* Ricerca */
.search-row {
	background: transparent !important;
}

.search-container {
	padding: 12px 16px;
	background: #f8f8f8;
	border-bottom: 1px solid #ddd;
}

.search-input {
	width: 100%;
	max-width: 300px;
	padding: 8px 12px;
	border: 1px solid #ccc;
	border-radius: 4px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s;
}

.search-input:focus {
	border-color: #008f8f;
}

/* Paginazione */
.pagination-controls {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 20px;
	/* margin-top: 24px; */
	padding: 16px;
	/* border-bottom: 1px solid #ddd; */
}

.btn-pagination {
	padding: 8px 16px;
	background: #008f8f;
	color: white;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-weight: 600;
	transition: background 0.2s;
}

.btn-pagination:hover:not(:disabled) {
	background: #00a7a7;
	/* color: #fff; */
}

.btn-pagination:disabled {
	background-color: #ccc;
	/* border-color: #0d0d0d; */
	color: #fff;
	cursor: not-allowed;
}

.pagination-info {
	font-size: 14px;
	color: #666;
	font-weight: 500;
}

.pagination-jump {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	color: #666;
	font-weight: 500;
}

.jump-input {
	width: 50px;
	padding: 4px 8px;
	border: 1px solid #ccc;
	border-radius: 4px;
	text-align: center;
	font-size: 14px;
	outline: none;
}

.jump-input:focus {
	border-color: #008f8f;
}

/* Rimuovi freccine input number */
.jump-input::-webkit-outer-spin-button,
.jump-input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	appearance: none;
	margin: 0;
}
.jump-input[type="number"] {
	-moz-appearance: textfield;
	appearance: textfield;
}

@media (max-width: 1023px) {
	.breadcrumb {
		padding: 0 45px;
	}
}

/* --- New Device Modal --- */
.new-device-modal {
	width: 450px;
}

.modal-label {
	display: block;
	font-size: 13px;
	color: #aaa;
	margin-bottom: 6px;
	font-weight: 600;
}

.btn-secondary {
	border: 1px solid #444;
	background: transparent;
	color: #ccc;
	transition: all 0.2s;
}

.btn-secondary:hover {
	border-color: #666;
	color: #fff;
	background: rgba(255, 255, 255, 0.05) !important;
}

.form-group {
	text-align: left;
}

.radio-label,
.checkbox-label {
	font-size: 14px;
	color: #eee;
	transition: color 0.2s;
}

.radio-label:hover,
.checkbox-label:hover {
	color: #fff;
}

.radio-group input,
.checkbox-group input {
	cursor: pointer;
}

.field-hint {
	margin-top: 6px;
	font-size: 12px;
	color: #999;
	font-style: italic;
}

.btn-otp {
	display: flex;
	align-items: center;
	gap: 8px;
	background: #008f8f;
	border: none;
	padding: 0 16px;
	height: 40px;
	border-radius: 6px;
	color: #fff;
	font-weight: 600;
	font-size: 14px;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.2s, transform 0.1s;
}

.btn-otp:hover:not(:disabled) {
	background: #00a7a7;
	transform: translateY(-1px);
}

.btn-otp:active:not(:disabled) {
	transform: translateY(0);
}

.btn-otp:disabled {
	opacity: 0.6;
	cursor: not-allowed;
	background: #444;
}

.btn-otp svg {
	width: 18px;
	height: 18px;
}

.animate-spin {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
</style>
